/**
 * Collections in Find: the user's collections of objects as Find offers them (GET /find/collections),
 * the searches that find a collection's objects one after another, and the words for saving into one.
 * Plain functions, no map.
 */

// "Gran, Fallen tree, Stump"
export const objectNames = collection =>
	(collection?.objects ?? []).map(object => object.name).join(', ');

const plural = (word, n) => (n === 1 || word.endsWith('s') ? word : `${word}s`);

/**
 * The line under a collection's name: "Analyzed · 225 objects" or "3 objects · not analyzed here",
 * and how many Find results went into it.
 * @param {Object} collection - A FindCollection
 * @returns {string}
 */
export const collectionNote = collection => {
	const objects = collection.objects?.length ?? 0;
	const parts = collection.analyzed
		? [`Analyzed · ${collection.total ?? 0} found`]
		: [`${objects} ${plural('object', objects)} · not analyzed here`];
	if (collection.find_saves) parts.push(`${collection.find_saves} by Find`);
	return parts.join(' · ');
};

/** Analyzed collections, then the others; each kept in the server's order. */
export const splitCollections = (collections = []) => ({
	analyzed: collections.filter(c => c.analyzed),
	others: collections.filter(c => !c.analyzed),
});

/**
 * Where Find is in a collection's searches: the search to type now, and what comes after it.
 * @param {Object|null} target - `{ collection, step }`
 * @returns {{ search: Object|null, next: Object|null, count: number, step: number }}
 */
export const collectionStep = target => {
	const searches = target?.collection?.searches ?? [];
	const step = Math.min(target?.step ?? 0, Math.max(searches.length - 1, 0));
	return {
		search: searches[step] ?? null,
		next: searches[step + 1] ?? null,
		count: searches.length,
		step,
	};
};

/**
 * The question Find asks once a collection is chosen: "Find Car, Building in this view?" with what
 * follows ("Road is searched next, as a line").
 * @param {Object} target - `{ collection, step }`
 * @param {string} where - "this view" | "the drawn area"
 * @returns {{ title: string, detail: string|null }}
 */
export const collectionQuestion = (target, where) => {
	const { search, next, count, step } = collectionStep(target);
	if (!search) return { title: `${target.collection.name} has no objects to find`, detail: null };
	const traced = search.mode === 'segment' ? ' (traced as lines and areas)' : '';
	const detail = next
		? `Search ${step + 1} of ${count}. ${next.text} ${next.mode === 'segment' ? 'is traced' : 'is searched'} next.`
		: count > 1
			? `Search ${step + 1} of ${count}.`
			: null;
	return { title: `Find ${search.text} in ${where}${traced}?`, detail };
};

/**
 * The Save button's words for a collection: "Save to Infrastructure".
 * @param {Object|null} collection
 * @returns {string}
 */
export const saveLabel = collection => (collection ? `Save to ${collection.name}` : 'Save results');

/**
 * The saved button's words: "Saved to Infrastructure · 46" or "Saved · 46 cars".
 * @param {Object} saved - FindSaved
 * @param {string} noun - What one result is called ("car")
 * @returns {string}
 */
export const savedLabel = (saved, noun) => {
	const count = saved.count ?? null;
	if (saved.collection) {
		const into = saved.collection.count ?? count;
		return into === null
			? `Saved to ${saved.collection.name}`
			: `Saved to ${saved.collection.name} · ${into}`;
	}
	if (count === null) return 'Saved';
	return `Saved · ${count} ${plural(noun, count)}`;
};

/**
 * What a save into a collection could not put in it: the labels naming none of its objects (saved
 * to the project only).
 * @param {Object} saved - FindSaved
 * @returns {string|null}
 */
export const unmatchedNote = saved => {
	const unmatched = saved?.collection?.unmatched ?? [];
	if (!unmatched.length) return null;
	return `${unmatched.join(', ')} ${unmatched.length === 1 ? 'is' : 'are'} not in ${saved.collection.name}; saved to the project only.`;
};
